import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff, KeyRound, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const { login, forgotPassword, pendingApproval } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      setLoginError('');
      if (result.forceReset) {
        toast.info('You must change your password before continuing.');
        navigate('/change-password');
      } else {
        toast.success('Welcome back!');
        navigate('/');
      }
    } else {
      setLoginError(result.error);
      setReportSent(false);
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    
    const result = await forgotPassword(forgotEmail);
    
    if (result.success) {
      toast.success('Your request has been sent to admin for approval.');
      setForgotOpen(false);
      setForgotEmail('');
    } else {
      toast.error(result.error);
    }
    
    setForgotLoading(false);
  };

  return (
    <main className="min-h-screen pt-24 pb-12 px-6 flex items-center justify-center bg-[#050505]">
      <div className="w-full max-w-md">
        <div className="bg-[#121212] border border-zinc-800 p-8" data-testid="login-form-container">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-bold uppercase text-white mb-2">
              Login
            </h1>
            <p className="text-zinc-500 text-sm">
              Access your A.C.C.T account
            </p>
          </div>

          {pendingApproval && (
            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 text-sm">
              Your account is pending admin approval. Please wait for confirmation.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-400 text-sm uppercase tracking-wide">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400 focus:ring-cyan-400"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-400 text-sm uppercase tracking-wide">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 pr-12 focus:border-cyan-400 focus:ring-cyan-400"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="text-right">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-sm text-cyan-400 hover:text-cyan-300"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
              data-testid="login-submit-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Logging in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-5 h-5" />
                  Login
                </span>
              )}
            </Button>

            {loginError && (
              <div className="mt-4 border border-red-500/30 bg-red-500/5 p-3" data-testid="login-error-section">
                <p className="text-red-400 text-sm mb-2">{loginError}</p>
                {!reportSent ? (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/error-report`, { email, error: loginError, type: 'login_error' });
                        setReportSent(true);
                        toast.success('Error report sent to admin');
                      } catch {
                        toast.error('Failed to send report');
                      }
                    }}
                    className="flex items-center gap-1.5 text-xs text-yellow-500 hover:text-yellow-400 transition-colors"
                    data-testid="report-login-error"
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Report this issue to admin
                  </button>
                ) : (
                  <p className="text-green-500 text-xs">Report sent. An admin will look into it.</p>
                )}
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-500 text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-cyan-400 hover:text-cyan-300" data-testid="register-link">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-cyan-400" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-6 mt-4">
            <p className="text-zinc-400 text-sm">
              Enter your email and a request will be sent to an admin. Once approved, your password will be reset and you can log in with the default password.
            </p>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">
                Email
              </Label>
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                data-testid="forgot-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={forgotLoading}
              className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
              data-testid="forgot-submit-button"
            >
              {forgotLoading ? 'Sending...' : 'Request Password Reset'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
